import React, { useEffect, useState } from "react";
import { Download, Eye, FileText } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { downloadPayslipPdf, pdfErrorMessage, PAYSLIP_PASSWORD_HINT } from "@/lib/payslipPdf";
import { PageHeader, Surface } from "@/components/app/Primitives";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import PayslipHours from "@/components/app/PayslipHours";
import { AllowanceDetail, DeductionDetail } from "@/components/app/PayslipDetail";

const php = (n) =>
  new Intl.NumberFormat("en-PH", { style: "currency", currency: "PHP" }).format(Number(n || 0));

const fmtDate = (d) => {
  if (!d) return "-";
  try {
    return new Date(d).toLocaleDateString("en-PH", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return d;
  }
};

function getPayDate(item) {
  return item.pay_date || item.payout_date || item.period_end || item.created_at;
}

function getNetPay(item) {
  return item.net_pay ?? item.total_net ?? item.amount ?? 0;
}

function Btn({ children, onClick, primary = false }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center rounded-md px-3 py-2 text-sm font-semibold ${
        primary
          ? "bg-primary text-primary-foreground hover:opacity-90"
          : "border border-border bg-background hover:bg-secondary"
      }`}
    >
      {children}
    </button>
  );
}

function Line({ label, value, bold = false, hideZero = false }) {
  if (hideZero && !Number(value || 0)) return null;
  return (
    <div className={`flex items-center justify-between py-2 ${bold ? "font-bold border-t mt-2 pt-3" : ""}`}>
      <span>{label}</span>
      <span>{php(value)}</span>
    </div>
  );
}

export default function MyPayslips() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/me/payslips");
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const view = async (item) => {
    try {
      const { data } = await api.get(`/me/payslips/${item.id}`);
      setSelected({ ...item, ...(data || {}) });
    } catch {
      setSelected(item);
    }
  };

  const employeeName = selected?.employee_name || selected?.name || "Employee";
  const employeeNo = selected?.employee_no || selected?.employee_id || "-";
  const position = selected?.employee?.position || selected?.employee?.title || selected?.position || selected?.job_title || "-";
  const department = selected?.employee?.department_name || selected?.employee?.department || selected?.department || "-";
  const period =
    selected?.period_start && selected?.period_end
      ? `${fmtDate(selected.period_start)} - ${fmtDate(selected.period_end)}`
      : "Payroll Period";

  const earningsTotal =
    Number(selected?.basic_pay || 0) +
    Number(selected?.ot_pay || 0) +
    Number(selected?.night_diff_pay || 0) +
    Number(selected?.hazard_pay || 0) +
    Number(selected?.allowances_taxable || 0) +
    Number(selected?.allowances_nontaxable || 0) +
    Number(selected?.other_earnings || 0);

  const deductionsTotal =
    Number(selected?.sss_ee || 0) +
    Number(selected?.philhealth_ee || 0) +
    Number(selected?.pagibig_ee || 0) +
    Number(selected?.withholding_tax || 0) +
    Number(selected?.loans || 0) +
    Number(selected?.other_deductions || 0);

  return (
    <div>
      <PageHeader title="My Payslips" subtitle="View and download released payslips." />

      <Surface className="overflow-hidden">
        <div className="border-b px-5 py-4 flex items-center justify-between">
          <div>
            <div className="font-semibold">Released Payslips</div>
            <div className="text-sm text-muted-foreground">Desktop table view</div>
          </div>
          <FileText className="size-5 text-muted-foreground" />
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Pay Date</th>
                <th className="text-left px-5 py-3 font-semibold">Period</th>
                <th className="text-left px-5 py-3 font-semibold">Type</th>
                <th className="text-right px-5 py-3 font-semibold">Net Pay</th>
                <th className="text-right px-5 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan="5" className="px-5 py-8 text-center text-muted-foreground">
                    Loading payslips...
                  </td>
                </tr>
              )}

              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan="5" className="px-5 py-8 text-center text-muted-foreground">
                    No payslips released yet.
                  </td>
                </tr>
              )}

              {items.map((item) => (
                <tr key={item.id} className="border-t">
                  <td className="px-5 py-4 font-medium">{fmtDate(getPayDate(item))}</td>
                  <td className="px-5 py-4 text-muted-foreground">
                    {item.period_start && item.period_end
                      ? `${fmtDate(item.period_start)} - ${fmtDate(item.period_end)}`
                      : "Payroll Payslip"}
                  </td>
                  <td className="px-5 py-4">
                    {item.run_type === "allowance"
                      ? <span className="rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 text-xs font-semibold">Allowance</span>
                      : <span className="rounded-full bg-slate-100 text-slate-700 border border-slate-200 px-2 py-0.5 text-xs font-semibold">Basic</span>}
                  </td>
                  <td className="px-5 py-4 text-right font-semibold">{php(getNetPay(item))}</td>
                  <td className="px-5 py-4">
                    <div className="flex justify-end gap-2">
                      <Btn onClick={() => view(item)}>
                        <Eye className="mr-2 size-4" /> View
                      </Btn>
                      <Btn primary onClick={() => view(item)}>
                        <Download className="mr-2 size-4" /> Download
                      </Btn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Surface>

      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Payslip — {fmtDate(getPayDate(selected || {}))}</DialogTitle>
          </DialogHeader>

          {selected && (
            <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
              <div className="border bg-white p-8 text-slate-950">
                <div className="flex items-start justify-between border-b pb-6">
                  <div>
                    <div className="text-2xl font-black">{selected.company?.name || "Company"}</div>
                    <div className="mt-1 text-sm text-slate-600">{selected.company?.address || ""}</div>
                    <div className="text-sm text-slate-600">TIN: {selected.company?.tin || "-"}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-4xl font-black tracking-tight">PAYSLIP</div>
                    <div className="mt-1 text-sm font-semibold text-slate-500">Confidential</div>
                  </div>
                </div>

                <div className="grid gap-8 border-b py-6 md:grid-cols-2">
                  <div className="space-y-2 text-sm">
                    <div className="grid grid-cols-2"><span className="text-slate-500">Employee No.</span><b>{employeeNo}</b></div>
                    <div className="grid grid-cols-2"><span className="text-slate-500">Employee Name</span><b>{employeeName}</b></div>
                    <div className="grid grid-cols-2"><span className="text-slate-500">Position</span><b>{position}</b></div>
                    <div className="grid grid-cols-2"><span className="text-slate-500">Department</span><b>{department}</b></div>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="grid grid-cols-2"><span className="text-slate-500">Pay Date</span><b>{fmtDate(getPayDate(selected))}</b></div>
                    <div className="grid grid-cols-2"><span className="text-slate-500">Pay Period</span><b>{period}</b></div>
                    <div className="grid grid-cols-2"><span className="text-slate-500">Pay Type</span><b>{selected.frequency || "Semi-Monthly"}</b></div>
                    <div className="grid grid-cols-2"><span className="text-slate-500">Company</span><b>{selected.company?.name || selected.company_name || "-"}</b></div>
                  </div>
                </div>

                <div className="grid gap-6 py-6 md:grid-cols-2">
                  <div>
                    <div className="bg-slate-100 px-3 py-2 font-black">EARNINGS</div>
                    <div className="mt-3 text-sm">
                      <Line label="Basic Pay" value={selected.basic_pay} />
                      <Line label="Overtime Pay" value={selected.ot_pay} />
                      <Line label="Rest Day Overtime Pay" value={selected.rest_day_ot_pay} hideZero />
                      <Line label="Special Holiday Overtime Pay" value={selected.special_day_ot_pay} hideZero />
                      <Line label="Regular Holiday Overtime Pay" value={selected.regular_holiday_ot_pay} hideZero />
                      <Line label="Night Differential" value={selected.night_diff_pay} />
                      <Line label="Holiday Pay" value={selected.holiday_pay} hideZero />
                      <Line label="Rest Day Pay" value={selected.rest_day_pay} hideZero />
                      <Line label="Hazard Pay" value={selected.hazard_pay} />
                      <Line label="Minimum Wage Adjustment" value={selected.minimum_wage_adjustment} hideZero />
                      {selected.detail ? <AllowanceDetail detail={selected.detail} /> : (
                        <>
                          <Line label="Allowances Taxable" value={selected.allowances_taxable} />
                          <Line label="Allowances Non-Taxable" value={selected.allowances_nontaxable} />
                          <Line label="Other Earnings" value={selected.other_earnings} />
                        </>
                      )}
                      <Line label="Gross Pay" value={selected.gross_pay || earningsTotal} bold />
                    </div>
                  </div>

                  <div>
                    <div className="bg-slate-100 px-3 py-2 font-black">DEDUCTIONS</div>
                    <div className="mt-3 text-sm">
                      {selected.detail ? <DeductionDetail detail={selected.detail} /> : (
                        <>
                          <Line label="SSS EE Share" value={selected.sss_ee} />
                          <Line label="PhilHealth EE Share" value={selected.philhealth_ee} />
                          <Line label="Pag-IBIG EE Share" value={selected.pagibig_ee} />
                          <Line label="Withholding Tax" value={selected.withholding_tax} />
                          <Line label="Loans" value={selected.loans} />
                          <Line label="Other Deductions" value={selected.other_deductions} />
                        </>
                      )}
                      <Line label="Total Deductions" value={selected.total_deductions || deductionsTotal} bold />
                    </div>
                  </div>
                </div>

                <PayslipHours block={selected.hours_block} className="mb-6" />

                <div className="mt-2 border-2 border-slate-950 p-5">
                  <div className="flex items-center justify-between">
                    <div className="text-2xl font-black">NET PAY</div>
                    <div className="text-3xl font-black">{php(selected.net_pay || getNetPay(selected))}</div>
                  </div>
                </div>

                <div className="mt-10 grid gap-8 text-sm md:grid-cols-2">
                  <div>
                    <div>Prepared by:</div>
                    <div className="mt-12 border-t pt-2 text-center">HR Department</div>
                  </div>
                  <div>
                    <div>Received by:</div>
                    <div className="mt-12 border-t pt-2 text-center">{employeeName}</div>
                    <div className="mt-4">Date: __________________</div>
                  </div>
                </div>

                <div className="mt-8 text-center text-xs text-slate-500">
                  This is a system-generated payslip and does not require a signature.
                </div>
              </div>

              <div className="space-y-4">
                {selected.company?.payslip_password_protect ? (
                  <>
                    <Btn primary onClick={async () => { try { await downloadPayslipPdf(selected.id); } catch (e) { toast.error(await pdfErrorMessage(e)); } }}>
                      <Download className="mr-2 size-4" /> Download PDF (password protected)
                    </Btn>
                    <div className="rounded-md border border-border bg-secondary/40 p-3 text-xs" data-testid="payslip-password-hint">{PAYSLIP_PASSWORD_HINT}</div>
                  </>
                ) : (
                  <>
                    <Btn primary onClick={() => window.print()}>
                      <Download className="mr-2 size-4" /> Download / Print PDF
                    </Btn>
                  </>
                )}

                <Surface className="p-5">
                  <div className="text-xs font-bold uppercase text-muted-foreground">Gross Pay</div>
                  <div className="mt-1 text-xl font-black">{php(selected.gross_pay || earningsTotal)}</div>
                </Surface>

                <Surface className="p-5">
                  <div className="text-xs font-bold uppercase text-muted-foreground">Total Deductions</div>
                  <div className="mt-1 text-xl font-black">{php(selected.total_deductions || deductionsTotal)}</div>
                </Surface>

                <Surface className="p-5 border-primary/30 bg-primary/5">
                  <div className="text-xs font-bold uppercase text-muted-foreground">Net Pay</div>
                  <div className="mt-1 text-2xl font-black">{php(selected.net_pay || getNetPay(selected))}</div>
                </Surface>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
